import { useEffect, useRef } from "react";
import { useDragLayer } from "react-dnd";

interface ScrollableContainerProps {
  children: React.ReactNode;
}

export function ScrollableContainer({ children }: ScrollableContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { isDragging, currentOffset } = useDragLayer((monitor) => ({
    currentOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging(),
  }));

  useEffect(() => {
    if (!isDragging || !currentOffset || !containerRef.current) return;

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const scrollThreshold = 100;
    const scrollSpeed = 15;

    const checkScroll = () => {
      if (!isDragging || !currentOffset) return;

      const mouseY = currentOffset.y;
      const distanceFromTop = mouseY - containerRect.top;
      const distanceFromBottom = containerRect.bottom - mouseY;

      if (distanceFromTop < scrollThreshold) {
        container.scrollTop -= scrollSpeed;
      } else if (distanceFromBottom < scrollThreshold) {
        container.scrollTop += scrollSpeed;
      }
    };

    const interval = setInterval(checkScroll, 16);
    return () => clearInterval(interval);
  }, [isDragging, currentOffset]);

  return (
    <div
      ref={containerRef}
      className="max-h-[450px] overflow-y-auto border rounded-md p-2 mb-[70px] border-gray-300 flex gap-2 flex-col relative"
      style={{ isolation: "isolate" }}
    >
      {children}
    </div>
  );
}
